/**
 * Device quality tier + per-world "lite" variants.
 *
 * One world, two renditions: desktop gets the world exactly as authored;
 * standalone headsets (Quest Browser, the immersive Quest app, Pico, Wolvic)
 * and phones/tablets get the world's lite rendition, when it has one.
 *
 * A world opts in with `settings.lite`:
 *   {
 *     urls: { "<original asset url>": "<optimized url>", ... },   // GLB, sky, images
 *     maxDpr?: 1.5,          // phone pixel-ratio cap (non-XR)
 *     bloom?: true,          // keep bloom on phones (cheaper composer); false drops it
 *     detailDistance?: 22,   // world-game pickups beyond this (m) draw only their beam + glow
 *   }
 * `urls` is applied to every string in the world's asset rows (environment,
 * skybox, content, primitive textures, world-game cutout images), so a variant
 * only needs to exist for the files worth optimizing. Originals are never
 * touched; desktop never reads `settings.lite`.
 *
 * Manual override for comparing: `?quality=lite` / `?quality=full` (kept for
 * the tab's lifetime in sessionStorage, so world hops keep it); `?quality=auto`
 * clears it.
 *
 * Pure helpers take an optional `env` so the unit tests can drive them
 * (tests/qualityTier.test.ts).
 */

export type QualityTier = 'full' | 'lite';

export const QUALITY_OVERRIDE_KEY = 'gx-quality';

export interface WorldLiteSettings {
  urls?: Record<string, string>;
  maxDpr?: number;
  bloom?: boolean;
  detailDistance?: number;
}

export interface TierEnv {
  search: string;
  userAgent: string;
  maxTouchPoints: number;
  /** isQuestImmersiveApp() result (the TWA reports a desktop-ish UA in some builds). */
  questApp: boolean;
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
}

const safeStorage = (): TierEnv['storage'] => {
  try { return typeof sessionStorage !== 'undefined' ? sessionStorage : null; } catch { return null; }
};

export function browserTierEnv(questApp = false): TierEnv {
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  return {
    search: typeof window !== 'undefined' ? window.location.search : '',
    userAgent: nav?.userAgent ?? '',
    maxTouchPoints: nav?.maxTouchPoints ?? 0,
    questApp,
    storage: safeStorage(),
  };
}

/** Standalone headset browsers. "GathrXR" is the Quest app's UA marker. */
export const HEADSET_UA = /OculusBrowser|Quest|Pico|Wolvic|GathrXR/i;
/** Phones and tablets (iPadOS reports a Mac UA, caught by touch points below). */
export const MOBILE_UA = /Android|iPhone|iPad|iPod|Mobile|Silk|Kindle/i;

const read = (s: TierEnv['storage'], k: string) => { try { return s?.getItem(k) ?? null; } catch { return null; } };

/** The `?quality=` override (also remembered for the tab), or null for automatic. */
export function qualityOverride(env: TierEnv): QualityTier | null {
  const q = new URLSearchParams(env.search).get('quality');
  try {
    if (q === 'full' || q === 'lite') env.storage?.setItem(QUALITY_OVERRIDE_KEY, q);
    else if (q === 'auto') env.storage?.removeItem(QUALITY_OVERRIDE_KEY);
  } catch { /* private mode */ }
  if (q === 'full' || q === 'lite') return q;
  if (q === 'auto') return null;
  const saved = read(env.storage, QUALITY_OVERRIDE_KEY);
  return saved === 'full' || saved === 'lite' ? saved : null;
}

/** True on headsets and phones/tablets: the devices the lite rendition is for. */
export function isLiteDevice(env: TierEnv): boolean {
  if (env.questApp) return true;
  if (HEADSET_UA.test(env.userAgent)) return true;
  if (MOBILE_UA.test(env.userAgent)) return true;
  // iPadOS 13+ desktop-class Safari: "Macintosh" UA, but a touch screen.
  return /Macintosh/i.test(env.userAgent) && env.maxTouchPoints > 1;
}

export function detectQualityTier(env: TierEnv): QualityTier {
  return qualityOverride(env) ?? (isLiteDevice(env) ? 'lite' : 'full');
}

/** A world's lite settings, or null when it has none (bad shapes ignored). */
export function parseLiteSettings(raw: unknown): WorldLiteSettings | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const out: WorldLiteSettings = {};
  if (r.urls && typeof r.urls === 'object') {
    const urls: Record<string, string> = {};
    for (const [k, v] of Object.entries(r.urls as Record<string, unknown>)) {
      if (k && typeof v === 'string' && v) urls[k] = v;
    }
    out.urls = urls;
  }
  if (typeof r.maxDpr === 'number' && r.maxDpr >= 0.5 && r.maxDpr <= 3) out.maxDpr = r.maxDpr;
  if (typeof r.bloom === 'boolean') out.bloom = r.bloom;
  if (typeof r.detailDistance === 'number' && r.detailDistance > 0) out.detailDistance = r.detailDistance;
  return out;
}

/**
 * Deep copy of `value` with every string that exactly equals a key of `urls`
 * replaced by its lite URL. Plain objects and arrays only; other values pass
 * through unchanged.
 */
export function applyLiteUrls<T>(value: T, urls: Record<string, string> | undefined): T {
  if (!urls || !Object.keys(urls).length) return value;
  const walk = (v: any): any => {
    if (typeof v === 'string') return Object.prototype.hasOwnProperty.call(urls, v) ? urls[v] : v;
    if (Array.isArray(v)) return v.map(walk);
    if (v && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype) {
      const o: Record<string, any> = {};
      for (const k of Object.keys(v)) o[k] = walk(v[k]);
      return o;
    }
    return v;
  };
  return walk(value);
}
